/**
 * React Hooks for Contacts
 *
 * This module provides custom hooks for managing contact state and operations
 * in React components using React Query for caching and synchronization.
 *
 * Query hooks use directApiClient for direct API calls (faster, no server action overhead).
 * Mutation hooks still use server actions for revalidation support.
 */

'use client';

import { useCallback } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  UseQueryOptions,
  UseInfiniteQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { FRESH_DATA_CONFIG, SMART_CACHE_CONFIG } from '../query-config';
import { createMutationHook, createOptimisticContext } from '../mutation-utils';
import { getDependencies } from '../query-dependencies';
import { directApiClient } from '@/lib/api/direct-client';
import {
  createContactAction,
  updateContactAction,
  deleteContactAction,
  confirmContactIdentityAction,
  markReadyToPayAction,
  markNotReadyToPayAction,
  toActionError
} from './client';
import {
  getContactsCacheKey,
  getContactCacheKey,
  getContactDisplayName,
  deriveIsBankVerified
} from './utils';
import type {
  Contact,
  ContactListResponse,
  CreateContactRequest,
  UpdateContactRequest,
  GlobalContactsFilters,
  GlobalContactsApiResponse,
  GlobalContactsResponse
} from './types';

/**
 * Extended Error type with optional validation errors
 * (Contacts service uses Record format from Zod flatten)
 */

// Query Hooks

/**
 * Hook to fetch all contacts for a matter
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useMatterContacts(
  matterId: number,
  options?: Omit<
    UseQueryOptions<ContactListResponse, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: getContactsCacheKey(matterId),
    queryFn: async () => {
      // Direct API call via directApiClient (bypasses proxy)
      const contacts = await directApiClient.get<Contact[]>(
        `/matters/${matterId}/contacts`
      );

      return {
        contacts,
        total: contacts.length
      } as ContactListResponse;
    },
    ...FRESH_DATA_CONFIG,
    ...options
  });
}

/**
 * Hook to fetch all contacts globally (not matter-specific)
 * Used for the global contacts list page with safe custody and matter associations
 */
export function useGlobalContacts(
  filters?: GlobalContactsFilters,
  options?: Omit<
    UseQueryOptions<GlobalContactsResponse, Error>,
    'queryKey' | 'queryFn'
  >
) {
  const page = filters?.page || 1;
  const limit = filters?.limit || 20;

  return useQuery({
    queryKey: ['contacts', 'global', filters],
    queryFn: async (): Promise<GlobalContactsResponse> => {
      // Build include parameter from boolean flags
      const includes: string[] = [];
      if (filters?.includeSafeCustodyPackets)
        includes.push('safeCustodyPackets');
      if (filters?.includeMatters) includes.push('matters');

      // Direct API call via directApiClient
      const response = await directApiClient.get<GlobalContactsApiResponse>(
        '/contacts',
        {
          params: {
            include: includes.length > 0 ? includes.join(',') : undefined,
            q: filters?.q,
            page,
            limit,
            sortBy: filters?.sortBy,
            sortOrder: filters?.sortOrder
          }
        }
      );

      // Compute totalPages from meta.totalItems
      const total = response.meta.totalItems;
      const totalPages = Math.ceil(total / limit);

      return {
        items: response.items,
        total,
        page,
        limit,
        totalPages
      };
    },
    ...SMART_CACHE_CONFIG,
    ...options
  });
}

/**
 * Hook to fetch global contacts with infinite loading
 * Used for the global contacts list page with load-more functionality
 */
export function useInfiniteGlobalContacts(
  filters?: Omit<GlobalContactsFilters, 'page'>,
  options?: Omit<
    UseInfiniteQueryOptions<GlobalContactsResponse, Error>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >
) {
  const limit = filters?.limit || 20;

  return useInfiniteQuery({
    queryKey: ['contacts', 'global', 'infinite', filters],
    queryFn: async ({ pageParam }): Promise<GlobalContactsResponse> => {
      const currentPage = pageParam as number;
      // Build include parameter from boolean flags
      const includes: string[] = [];
      if (filters?.includeSafeCustodyPackets)
        includes.push('safeCustodyPackets');
      if (filters?.includeMatters) includes.push('matters');

      // Direct API call via directApiClient
      const response = await directApiClient.get<GlobalContactsApiResponse>(
        '/contacts',
        {
          params: {
            include: includes.length > 0 ? includes.join(',') : undefined,
            q: filters?.q,
            page: currentPage,
            limit,
            sortBy: filters?.sortBy,
            sortOrder: filters?.sortOrder
          }
        }
      );

      // Compute totalPages from meta.totalItems
      const total = response.meta.totalItems;
      const totalPages = Math.ceil(total / limit);

      return {
        items: response.items,
        total,
        page: currentPage,
        limit,
        totalPages
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    ...SMART_CACHE_CONFIG,
    ...options
  });
}

/**
 * Hook to fetch a single contact
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useContact(
  matterId: number,
  contactId: number,
  options?: Omit<UseQueryOptions<Contact, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: getContactCacheKey(matterId, contactId),
    queryFn: async () => {
      // Direct API call via directApiClient (bypasses proxy)
      return directApiClient.get<Contact>(
        `/matters/${matterId}/contacts/${contactId}`
      );
    },
    ...FRESH_DATA_CONFIG,
    ...options
  });
}

// Mutation Hooks

/**
 * Hook to create a new contact
 */
export function useCreateContact(
  matterId: number,
  options?: UseMutationOptions<Contact, Error, CreateContactRequest>
) {
  const useHook = createMutationHook<Contact, CreateContactRequest>({
    mutationFn: async (data) => {
      const result = await createContactAction(matterId, data);
      if (!result.success) {
        throw toActionError(result, 'Failed to create contact');
      }
      return result.data!;
    },
    // Invalidate queries to mark them stale (works for inactive queries too)
    invalidateKeys: getDependencies('contact', matterId),
    // Also refetch active queries for immediate UI update
    refetchKeys: getDependencies('contact', matterId),
    onSuccessMessage: (contact) =>
      `Contact "${getContactDisplayName(contact)}" added successfully`,
    onErrorMessage: 'Failed to create contact'
  });

  return useHook(options);
}

/**
 * Hook to update a contact with optimistic updates
 */
export function useUpdateContact(
  matterId: number,
  contactId: number,
  options?: UseMutationOptions<
    Contact,
    Error,
    UpdateContactRequest,
    { snapshot: ContactListResponse | undefined }
  >
) {
  const queryClient = useQueryClient();

  const useHook = createMutationHook<
    Contact,
    UpdateContactRequest,
    { snapshot: ContactListResponse | undefined }
  >({
    mutationFn: async (data) => {
      const result = await updateContactAction(matterId, contactId, data);
      if (!result.success) {
        throw toActionError(result, 'Failed to update contact');
      }
      return result.data!;
    },
    // Invalidate queries to mark them stale (works for inactive queries too)
    invalidateKeys: getDependencies('contact', matterId, contactId),
    // Also refetch active queries for immediate UI update
    refetchKeys: getDependencies('contact', matterId, contactId),
    optimisticUpdate: (updates) => {
      const cacheKey = getContactsCacheKey(matterId);
      const context = createOptimisticContext<ContactListResponse>(
        queryClient,
        cacheKey
      );

      // Apply optimistic update
      context.update((old) => {
        if (!old) return old;
        return {
          ...old,
          contacts: old.contacts.map((contact) =>
            contact.id === contactId
              ? ({ ...contact, ...updates } as Contact)
              : contact
          )
        };
      });

      return { snapshot: context.snapshot };
    },
    onSuccessMessage: () => 'Contact updated successfully',
    onErrorMessage: 'Failed to update contact'
  });

  return useHook(options);
}

/**
 * Hook to delete a contact with optimistic removal
 */
export function useDeleteContact(
  matterId: number,
  options?: UseMutationOptions<
    void,
    Error,
    number,
    { snapshot: ContactListResponse | undefined }
  >
) {
  const queryClient = useQueryClient();

  const useHook = createMutationHook<
    void,
    number,
    { snapshot: ContactListResponse | undefined }
  >({
    mutationFn: async (contactId) => {
      const result = await deleteContactAction(matterId, contactId);
      if (!result.success) {
        throw toActionError(result, 'Failed to delete contact');
      }
    },
    // Invalidate queries to mark them stale (works for inactive queries too)
    invalidateKeys: getDependencies('contact', matterId),
    // Also refetch active queries for immediate UI update
    refetchKeys: getDependencies('contact', matterId),
    optimisticUpdate: (contactId) => {
      const cacheKey = getContactsCacheKey(matterId);
      const context = createOptimisticContext<ContactListResponse>(
        queryClient,
        cacheKey
      );

      // Optimistically remove the contact
      context.update((old) => {
        if (!old) return old;
        return {
          ...old,
          contacts: old.contacts.filter((contact) => contact.id !== contactId),
          total: Math.max(0, old.total - 1)
        };
      });

      return { snapshot: context.snapshot };
    },
    onSuccessMessage: () => 'Contact deleted successfully',
    onErrorMessage: 'Failed to delete contact'
  });

  return useHook(options);
}

/**
 * Hook to manually confirm a contact's identity without an ID document.
 *
 * Identity axis only — it never touches bank verification. The `note` is
 * mandatory (the backend rejects empty / whitespace-only); callers must gate
 * their submit on a non-empty trimmed value.
 */
export function useConfirmContactIdentity(
  matterId: number,
  options?: UseMutationOptions<
    Contact,
    Error,
    { contactId: number; note: string }
  >
) {
  const useHook = createMutationHook<
    Contact,
    { contactId: number; note: string }
  >({
    mutationFn: async ({ contactId, note }) => {
      const result = await confirmContactIdentityAction(matterId, contactId, {
        note
      });
      if (!result.success) {
        throw toActionError(result, 'Failed to confirm identity');
      }
      return result.data!;
    },
    invalidateKeys: getDependencies('contact', matterId),
    refetchKeys: getDependencies('contact', matterId),
    onSuccessMessage: () => 'Identity confirmed',
    onErrorMessage: 'Failed to confirm identity'
  });

  return useHook(options);
}

/**
 * Mark a beneficiary ready to pay — the explicit, reversible lawyer confirmation
 * (with a mandatory file note) that drives Ready-to-Pay membership in the
 * reworked beneficiary tab. Eligibility is gated server-side; an ineligible
 * beneficiary throws (409 `NOT_ELIGIBLE_FOR_PAYMENT`). Invalidates the contact
 * keys so the beneficiary list / drawer re-derive their group on success.
 */
export function useMarkReadyToPay(
  matterId: number,
  options?: UseMutationOptions<
    Contact,
    Error,
    { contactId: number; note: string }
  >
) {
  const useHook = createMutationHook<
    Contact,
    { contactId: number; note: string }
  >({
    mutationFn: async ({ contactId, note }) => {
      const result = await markReadyToPayAction(matterId, contactId, { note });
      if (!result.success) {
        throw toActionError(result, 'Failed to mark ready to pay');
      }
      return result.data!;
    },
    invalidateKeys: getDependencies('contact', matterId),
    refetchKeys: getDependencies('contact', matterId),
    onSuccessMessage: () => 'Marked ready to pay',
    onErrorMessage: 'Failed to mark ready to pay'
  });

  return useHook(options);
}

/**
 * Clear a beneficiary's ready-to-pay confirmation (demote back to Review
 * Details). The reverse of {@link useMarkReadyToPay}; the optional reason rides
 * the audit event only. This hook is the published seam for BENTAB-7's
 * Ready-to-Pay drawer (the "Mark Not Ready to Pay" action).
 */
export function useMarkNotReadyToPay(
  matterId: number,
  options?: UseMutationOptions<
    Contact,
    Error,
    { contactId: number; note?: string }
  >
) {
  const useHook = createMutationHook<
    Contact,
    { contactId: number; note?: string }
  >({
    mutationFn: async ({ contactId, note }) => {
      const result = await markNotReadyToPayAction(matterId, contactId, {
        note
      });
      if (!result.success) {
        throw toActionError(result, 'Failed to mark not ready to pay');
      }
      return result.data!;
    },
    invalidateKeys: getDependencies('contact', matterId),
    refetchKeys: getDependencies('contact', matterId),
    onSuccessMessage: () => 'Marked not ready to pay',
    onErrorMessage: 'Failed to mark not ready to pay'
  });

  return useHook(options);
}

// Composite Hooks

/**
 * Hook for generic contact update (takes contactId as parameter)
 *
 * `showToast` exists for the **commit-on-blur** attribute surface (LF-6306 /
 * [ATTR-5]). A whole-form Save happens once and its "Contact updated
 * successfully" toast is the confirmation; a drawer with no Save button commits
 * every field as the user leaves it, so the same toast fires per field —
 * editing six rows stacks six of them (the dedupe window is 500ms, far shorter
 * than a person typing). The shipped attribute surface, the enquiry drawer, is
 * silent for exactly this reason, and the treatment's whole argument is that it
 * reads as a quiet record.
 *
 * Defaults to `true`, so every existing caller is unchanged.
 */
export function useGenericUpdateContact(
  matterId: number,
  options?: UseMutationOptions<
    Contact,
    Error,
    { contactId: number; data: UpdateContactRequest },
    { snapshot: ContactListResponse | undefined }
  >,
  { showToast = true }: { showToast?: boolean } = {}
) {
  const queryClient = useQueryClient();

  const useHook = createMutationHook<
    Contact,
    { contactId: number; data: UpdateContactRequest },
    { snapshot: ContactListResponse | undefined }
  >({
    mutationFn: async ({ contactId, data }) => {
      const result = await updateContactAction(matterId, contactId, data);
      if (!result.success) {
        throw toActionError(result, 'Failed to update contact');
      }
      return result.data!;
    },
    // Invalidate queries to mark them stale (works for inactive queries too)
    invalidateKeys: getDependencies('contact', matterId),
    // Also refetch active queries for immediate UI update
    refetchKeys: getDependencies('contact', matterId),
    optimisticUpdate: ({ contactId, data }) => {
      const cacheKey = getContactsCacheKey(matterId);
      const context = createOptimisticContext<ContactListResponse>(
        queryClient,
        cacheKey
      );

      // Apply optimistic update
      context.update((old) => {
        if (!old) return old;
        return {
          ...old,
          contacts: old.contacts.map((contact) =>
            contact.id === contactId
              ? ({
                  ...contact,
                  ...data,
                  /*
                    The bank row writes `bankAccount.isVerified` and reads the
                    matter-scoped `isBankVerified` the server derives from it.
                    The create buffer and the review adapter both derive it
                    through this same helper; the edit merge did not, so the
                    Verified tick painted `No` for the whole round trip after
                    being ticked (VALE-1505, the VALE-632 defect on the other
                    path).
                  */
                  ...(data.bankAccount !== undefined
                    ? { isBankVerified: deriveIsBankVerified(data.bankAccount) }
                    : {})
                } as Contact)
              : contact
          )
        };
      });

      return { snapshot: context.snapshot };
    },
    onSuccessMessage: () => 'Contact updated successfully',
    onErrorMessage: 'Failed to update contact',
    showToast
  });

  return useHook(options);
}

/**
 * Hook that combines all contact operations for a matter
 */
export function useMatterContactsService(matterId: number) {
  const queryClient = useQueryClient();

  // Queries
  const contactsQuery = useMatterContacts(matterId);

  // Mutations
  const createMutation = useCreateContact(matterId);
  const updateMutation = useGenericUpdateContact(matterId);
  const deleteMutation = useDeleteContact(matterId);
  /*
    The same update, without the success toast — for the attribute drawer's
    per-field commit-on-blur (LF-6306). See `useGenericUpdateContact`'s
    `showToast` for why a per-field save must be quiet. Everything else is
    identical, including the optimistic merge and the invalidation set, so a
    committed field lands in the list cache exactly as a whole-form save does.
  */
  const commitFieldsMutation = useGenericUpdateContact(matterId, undefined, {
    showToast: false
  });

  // Helper functions
  const getContact = useCallback(
    (contactId: number) => {
      const data = queryClient.getQueryData<ContactListResponse>(
        getContactsCacheKey(matterId)
      );
      return data?.contacts.find((c) => c.id === contactId);
    },
    [matterId, queryClient]
  );

  const refreshContacts = useCallback(() => {
    return queryClient.refetchQueries({
      queryKey: getContactsCacheKey(matterId)
    });
  }, [matterId, queryClient]);

  const createContact = useCallback(
    async (data: CreateContactRequest) => {
      return createMutation.mutateAsync(data);
    },
    [createMutation]
  );

  const updateContact = useCallback(
    async (contactId: number, data: UpdateContactRequest) => {
      return updateMutation.mutateAsync({ contactId, data });
    },
    [updateMutation]
  );

  /**
   * Commit one field's patch — the attribute drawer's save path (LF-6306).
   *
   * Identical to {@link updateContact} but silent on success. It **rejects on
   * failure**, which is load-bearing rather than incidental: the attribute
   * treatment's §2 commit path surfaces a failed save by the promise rejecting,
   * and a swallowed rejection reads exactly like a save that worked.
   */
  const commitContactFields = useCallback(
    async (contactId: number, data: UpdateContactRequest) => {
      return commitFieldsMutation.mutateAsync({ contactId, data });
    },
    [commitFieldsMutation]
  );

  const deleteContact = useCallback(
    async (contactId: number) => {
      return deleteMutation.mutateAsync(contactId);
    },
    [deleteMutation]
  );

  return {
    // Data
    contacts: contactsQuery.data?.contacts || [],
    total: contactsQuery.data?.total || 0,

    // Loading states
    isLoading: contactsQuery.isLoading,
    isError: contactsQuery.isError,
    error: contactsQuery.error,

    // Operations
    createContact,
    updateContact,
    commitContactFields,
    deleteContact,
    getContact,
    refreshContacts,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
}
